import { apiFetch } from "./api";
import { getSession } from "./auth";
import { alCambiarDeSesion } from "./estadoDeSesion";

/**
 * Anuncios y mensajes, por residencial.
 *
 * Vivían en `localStorage`, y eso los hacía inútiles: cada navegador tenía los
 * suyos, así que la bibliotecaria publicaba un aviso y no lo veía NADIE — ni el
 * residente en el kiosco de al lado, ni ella misma desde otra estación. Ahora son
 * de la sede, como los eventos, y salen de la tabla `notifications`.
 *
 * Quién ve qué lo decide el servidor a partir del token: un participante recibe
 * los de su residencial dirigidos a él o a su grupo; el staff ve los de su
 * alcance, incluidos los apagados (para poder reactivarlos).
 */

/*
 * Los mismos límites que aplica el servidor. Duplicados a propósito y no
 * importados del backend —son paquetes distintos— pero si divergen, el síntoma
 * es benigno: el navegador deja escribir y el servidor lo rechaza con un mensaje
 * que se entiende.
 */
export const TITULO_MAX = 120;
export const CUERPO_MAX = 2000;

export type Audience = "sede" | "tipo" | "usuario";
export type Tipo = "nino" | "joven" | "adulto";

export interface Message {
  id: string;
  title: string;
  body: string;
  audience: Audience;
  tipo: Tipo | null;
  active: boolean;
  sentAt: string;
  toUserId: string | null;
  toUserName: string | null;
  /** null = anuncio de toda la organización, no de un residencial concreto. */
  siteCode: string | null;
  siteName: string | null;
  /** El mismo anuncio publicado en varios residenciales a la vez. */
  grupoId: string | null;
  /** Rol de quien lo publicó: el anuncio general del super_admin solo lo maneja él. */
  fromRole: string | null;
  /** Solo con `agrupar`: en cuántos residenciales de tu alcance está y si sigue
   *  encendido en alguno. */
  sedes?: number;
  algunoActivo?: boolean;
}

export const AUD_LABEL: Record<Tipo | "sede", string> = {
  sede: "Todo el residencial",
  nino: "Niños",
  joven: "Jóvenes",
  adulto: "Adultos",
};

/*
 * Sin caché local. La tenía para que un kiosco sin red siguiera enseñando avisos
 * viejos; con conexión garantizada, eso solo servía para mostrar un anuncio ya
 * retirado.
 */

function mapRow(r: any): Message {
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    audience: r.audience,
    tipo: r.tipo ?? null,
    active: r.active !== false,
    sentAt: r.sent_at ?? r.sentAt ?? new Date().toISOString(),
    toUserId: r.toUserId ?? null,
    toUserName: r.toUserName ?? null,
    siteCode: r.siteCode ?? null,
    siteName: r.siteName ?? null,
    grupoId: r.grupoId ?? null,
    fromRole: r.fromRole ?? null,
    sedes: typeof r.sedes === "number" ? r.sedes : undefined,
    algunoActivo: typeof r.algunoActivo === "boolean" ? r.algunoActivo : undefined,
  };
}

/**
 * Los mensajes del alcance. `siteCode` filtra a un residencial (los anuncios de
 * organización se incluyen igual: apagar uno global desde la vista de un
 * residencial es legítimo, y esconderlo lo haría invisible).
 */
export async function cargarMensajes(
  opts: { siteCode?: string; soloActivos?: boolean; agrupar?: boolean } = {}
): Promise<Message[]> {
  const qs = new URLSearchParams();
  if (opts.siteCode) qs.set("siteCode", opts.siteCode);
  if (opts.soloActivos) qs.set("active", "1");
  if (opts.agrupar) qs.set("agrupar", "1");
  const url = qs.toString() ? `/messages?${qs}` : "/messages";
  /*
   * Un fallo LANZA. Antes devolvía `[]`, y el panel no distinguía "no hay
   * anuncios" de "no se pudieron cargar": la bibliotecaria veía "No hay anuncios
   * publicados" con la API caída y volvía a publicar el mismo aviso.
   */
  let r: Response;
  try {
    r = await apiFetch(url);
  } catch (e: any) {
    throw new Error(`No se pudieron cargar los anuncios (${e?.message ?? "sin conexión"}).`);
  }
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(`No se pudieron cargar los anuncios (${d.error ?? `HTTP ${r.status}`}).`);
  }
  const d = await r.json();
  return ((d.messages ?? []) as any[]).map(mapRow);
}

/**
 * Igual, pero un fallo devuelve vacío. Solo para la franja de avisos del
 * participante: ahí no hay nada que hacer con el error, y una franja vacía es
 * mejor que un mensaje técnico encima de su lección. El panel usa
 * `cargarMensajes`, que sí lo dice.
 */
export async function fetchMessages(
  opts: { siteCode?: string; soloActivos?: boolean } = {}
): Promise<Message[]> {
  try {
    return await cargarMensajes(opts);
  } catch {
    return [];
  }
}

export interface NewMessage {
  title: string;
  body: string;
  audience: Audience;
  tipo?: Tipo;
  toUserId?: string;
  /** Omitido por admin/super_admin = anuncio a toda la organización. Lo ignora el
   *  servidor si quien publica es bibliotecaria: publica en su sede y punto. */
  siteCode?: string;
}

export async function addMessage(m: NewMessage): Promise<{
  ok: boolean;
  email?: { configured: boolean; queued: number; failed: number };
  error?: string;
}> {
  const result = await escribir("/messages", "POST", m);
  return result.ok ? { ok: true, email: result.data?.email } : { ok: false, error: result.error };
}

/*
 * `grupo`: el cambio va a TODAS las copias del anuncio (una por residencial)
 * que caen en tu alcance, no solo a esta.
 */
export async function toggleMessage(id: string, active: boolean, opts: { grupo?: boolean } = {}): Promise<{ ok: boolean; error?: string }> {
  return await escribir(`/messages/${encodeURIComponent(id)}`, "PATCH", { active, grupo: !!opts.grupo });
}

export async function editMessage(
  id: string, cambios: { title: string; body: string }, opts: { grupo?: boolean } = {}
): Promise<{ ok: boolean; error?: string }> {
  return await escribir(`/messages/${encodeURIComponent(id)}`, "PATCH", { ...cambios, grupo: !!opts.grupo });
}

export async function removeMessage(id: string, opts: { grupo?: boolean } = {}): Promise<{ ok: boolean; error?: string }> {
  return await escribir(`/messages/${encodeURIComponent(id)}${opts.grupo ? "?grupo=1" : ""}`, "DELETE");
}

async function escribir(path: string, method: string, body?: unknown) {
  try {
    const r = await apiFetch(path, {
      method,
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return { ok: false, error: d.error ?? `HTTP ${r.status}` };
    }
    const data = await r.json().catch(() => ({}));
    return { ok: true, data };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "sin conexión" };
  }
}

/**
 * Lo que el panel dice después de publicar.
 *
 * "En cola de envío", no "se avisó" ni "enviados": el servidor solo deja los
 * correos en la cola, y de ahí a la bandeja de alguien hay otro paso que puede
 * fallar — y mientras el servicio de correo esté en modo de prueba, solo llegan a
 * direcciones verificadas. Esta pantalla no sabe si alguien lo recibió, así que
 * no lo promete. Sin correo configurado se dice tal cual: no salió ninguno.
 */
export function textoDeEntrega(email?: { configured: boolean; queued: number; failed: number }): string {
  if (!email?.configured) {
    return "Publicado en la plataforma. No se envió ningún correo: el envío de correo no está configurado.";
  }
  const n = email.queued;
  const fallidos = email.failed ? ` · ${email.failed} no se pudieron poner en cola` : "";
  return `Publicado en la plataforma · ${n} ${n === 1 ? "correo" : "correos"} en cola de envío${fallidos}.`;
}

/** A quién va dirigido, para la etiqueta de la lista del panel. */
export function targetLabel(m: Message): string {
  if (m.audience === "usuario") return `Para: ${m.toUserName ?? "una persona"}`;
  if (m.audience === "tipo") return `Audiencia: ${AUD_LABEL[m.tipo ?? "sede"]}`;
  return "Audiencia: todo el residencial";
}

/** Dónde se publicó: un residencial, o todos. */
export function scopeLabel(m: Message): string {
  if ((m.sedes ?? 1) > 1) return `En ${m.sedes} residenciales`;
  return m.siteName ?? "Todos los residenciales";
}

// --- Descarte local de avisos (por sesión y por persona, en el equipo) ------
/*
 * La clave lleva a la PERSONA. Era una sola para la pestaña, y en un equipo
 * compartido el aviso que cerró uno se le escondía al siguiente que entraba sin
 * cerrar el navegador — justo el aviso que a lo mejor iba dirigido a él.
 * El usuario va con hash, como en learningProgress: separa las cuentas sin dejar
 * el identificador en claro.
 */
const DISMISS_PREFIX = "ikids.messages.dismissed";

function dismissKey(): string {
  const username = getSession()?.user ?? "anonymous";
  let hash = 2166136261;
  for (let i = 0; i < username.length; i += 1) {
    hash ^= username.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${DISMISS_PREFIX}.${(hash >>> 0).toString(36)}`;
}

// Y al cambiar de sesión se olvida todo lo descartado: cerrar un aviso vale para
// esa sesión, no para la próxima vez que la misma persona entre.
alCambiarDeSesion(() => {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(DISMISS_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch { /* modo privado o cuota */ }
});

export function dismissed(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(dismissKey()) ?? "[]"));
  } catch {
    return new Set();
  }
}

export function dismiss(id: string): void {
  const d = dismissed();
  d.add(id);
  try { sessionStorage.setItem(dismissKey(), JSON.stringify([...d])); } catch { /* modo privado o cuota */ }
  /*
   * Y en el servidor, para que no vuelva al entrar otra vez ni en otra
   * computadora. Solo el participante: el kiosco es compartido (lo que cierra
   * un visitante no se le cierra al siguiente) y su lista blanca no incluye
   * esta ruta. Si falla, el cierre local de esta pestaña sigue valiendo.
   */
  if (getSession()?.cognitoRole === "participant") {
    void apiFetch(`/messages/${encodeURIComponent(id)}/cerrar`, { method: "POST" }).catch(() => {});
  }
}
